import "../assets/css/Dashboard.css";

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import {
  embedDashboard,
  EmbedDashboardParams,
} from "../modules/superset-embedded-sdk";

import { AxiosResponse } from "axios";
import { getEmbeddedNativeFilters } from "../utils/embeddedNativeFilters";
import { DashboardInfo } from "../utils/types";

import { baseUrl, supersetBaseUrl } from "../config";
import axiosConfig from "../utils/axiosConfig";

import { Box, Stack, Paper, useTheme } from "@mui/material";
import ExploreDataMenu from "./ExploreDataMenu";
import Loader from "./Loader";

interface IEmbedded {
  readonly dashboardInfo: DashboardInfo;
}

function Embedded({ dashboardInfo }: Readonly<IEmbedded>) {
  const theme = useTheme();

  const { id, dashboard_uuid, hide_chart_controls, title } = dashboardInfo;

  const [queryParameters] = useSearchParams();
  const nativeFilters = queryParameters.get("native_filters");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dashboard_uuid) return;

    let cancelled = false;
    let embeddedDashboard: Awaited<ReturnType<typeof embedDashboard>> | undefined;
    setLoading(true);

    const fetchGuestToken = async (_uuid: string) => {
      try {
        // Make a GET request to the Superset API to obtain the guest token
        const response: AxiosResponse = await axiosConfig({
          method: "get",
          url: `${baseUrl}/guest_token/?uuid=${_uuid}`,
        });

        // Check if the response status is OK (200)
        if (response.status === 200 && response.data.guest_token) {
          // Extract the guest token from the response data
          const token: string = response.data.guest_token;
          return token;
        } else {
          throw new Error("Failed to fetch the guest token.");
        }
      } catch (error) {
        console.error(error);
        throw new Error("An error occurred while fetching the guest token");
      }
    };

    const embed = async () => {
      const mountPoint = document.getElementById("my-superset-container");
      if (!mountPoint) {
        setLoading(false);
        return;
      }

      const embedOptions: EmbedDashboardParams = {
        id: dashboard_uuid,
        supersetDomain: supersetBaseUrl!,
        mountPoint,
        fetchGuestToken: async () => fetchGuestToken(dashboard_uuid),
        dashboardUiConfig: {
          hideTitle: false,
          hideChartControls: hide_chart_controls,
          hideTab: false,
          filters: { expanded: true },
        },
      };

      try {
        embedOptions.nativeFilters =
          nativeFilters || (await getEmbeddedNativeFilters(title, id));
        embeddedDashboard = await embedDashboard(embedOptions);
        if (cancelled) {
          embeddedDashboard.unmount();
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void embed();

    return () => {
      cancelled = true;
      embeddedDashboard?.unmount();
    };
  }, [id, dashboard_uuid, hide_chart_controls, nativeFilters, title]);

  return (
    <Box sx={{ px: 3, py: 0 }}>
      <Box
        sx={{
          position: "absolute",
          top: theme.spacing(14),
          right: theme.spacing(4),
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
        >
          {dashboardInfo?.charts?.length > 0 && (
            <ExploreDataMenu dashboardInfo={dashboardInfo} />
          )}
        </Stack>
      </Box>
      <Box sx={{ position: "relative" }}>
        <Paper
          id="my-superset-container"
          sx={{
            height: `calc(100vh - ${theme.spacing(22)})`,
            borderRadius: "5px",
            border: "0px",
            boxShadow: "none",
            backgroundColor: "#f8f7fc",
          }}
        />
        {loading && (
          <Stack
            data-testid="embed-loader"
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "5px",
              backgroundColor: "#f8f7fc",
            }}
          >
            <Loader />
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export default Embedded;
